import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Icons from '../../Icons'
import DownloadIcon from '../../FloatingInspectorButton/FloatingIcons/DownloadIcon'
import EditIcon from '@/components/icons/EditIcon'
import ContextMenuPanel, { ContextMenuPanelAction } from '../../ContextMenu/ContextMenuPanel'
import { OPEN_EDITOR_IMAGE_FROM_CURRENT_PAGE, useOpenInEditor } from '@/hooks/useOpenInEditor'
import { INDEXED_IMAGE_ACTIONS } from '@/utils/indexedImage'
import { useEditorContext } from '@/scenes/engine'
import { FORMAT_OPTIONS } from '../../Navbar/components/downloadTypes'

interface DownloadStateMenuProps {
  close: () => void
  numberOfSelected?: number
}

function DownloadStateMenu({
  close,
  numberOfSelected = 1,
}: DownloadStateMenuProps) {
  const { t } = useTranslation()
  const isMulti = numberOfSelected > 1
  const { openInEditor } = useOpenInEditor()
  const { editor } = useEditorContext()
  const sceneCount = editor?.handlers?.pagesHandler?.scenes?.length ?? 0
  const canDeleteScene = sceneCount > 1

  const handleDelete = useCallback(() => {
    if (!canDeleteScene) return
    editor?.handlers?.pagesHandler?.deleteScene()
    close()
  }, [editor, close])

  const handleDownload = useCallback(async () => {
    const selectedSceneIds = editor?.handlers?.pagesHandler?.selectedSceneIds
    if (!selectedSceneIds?.length) return
    await editor.handlers.downloadHandler.downloadSelectedScenes(selectedSceneIds, 'DownloadStateMenu', 'AI_models', FORMAT_OPTIONS['jpeg'])
    close()
  }, [editor, close])

  const SINGLE_MENU_ACTIONS: ContextMenuPanelAction[] = [
    {
      id: 'open-editor',
      title: t('Open in editor'),
      icon: EditIcon,
      onClick: () => { openInEditor(OPEN_EDITOR_IMAGE_FROM_CURRENT_PAGE, INDEXED_IMAGE_ACTIONS.OPEN_EDITOR_AI_FASHION_MODELS); close() },
    },
    {
      id: 'delete',
      title: t('Delete'),
      icon: Icons.Delete,
      enhancer: 'DEL',
      onClick: handleDelete,
      disabled: !canDeleteScene,
    },
    {
      id: 'download',
      title: t('Download'),
      icon: DownloadIcon,
      onClick: handleDownload,
    },
  ]

  const MULTI_MENU_ACTIONS: ContextMenuPanelAction[] = [
    {
      id: 'download',
      title: `${t('Download')} (${numberOfSelected})`,
      icon: DownloadIcon,
      onClick: handleDownload,
    },
    {
      id: 'delete',
      title: `${t('Delete')} (${numberOfSelected})`,
      icon: Icons.Delete,
      enhancer: 'DEL',
      onClick: handleDelete,
    },
  ]

  return <ContextMenuPanel items={isMulti ? MULTI_MENU_ACTIONS : SINGLE_MENU_ACTIONS} />
}

export default DownloadStateMenu
